import React, { type ReactNode, createContext, useMemo, useState } from "react";
import type { TableProps } from "reactstrap";
import Table from "reactstrap/lib/Table";
import styled from "styled-components";
import { StringSort } from "./RaangeDataSortStrategies";
import type {
  RaangeDataTableSortDirection,
  RaangeDataTableSortStrategy,
} from "./RaangeDataTableSort";

interface IRaangeDataTableContext {
  sortSettings: IRaangeDataTableSortSettings;
  setSortSettings: (sortSettings: IRaangeDataTableSortSettings) => void;
}

interface IRaangeDataTableSortSettings {
  sortKey: string | undefined;
  sortDirection: RaangeDataTableSortDirection | undefined;
  sortStrategy: RaangeDataTableSortStrategy<any> | undefined;
}

export const RaangeDataTableContext = createContext<
  IRaangeDataTableContext | undefined
>(undefined);

export interface IRaangeDataTableProps<T> extends TableProps {
  data: T[];
  children: (processedData: T[]) => ReactNode;
}

export const RaangeDataTable = <T,>(props: IRaangeDataTableProps<T>) => {
  const { className, children, data, ...rest } = props;

  const [sortSettings, setSortSettings] =
    useState<IRaangeDataTableSortSettings>({
      sortKey: undefined,
      sortDirection: undefined,
      sortStrategy: undefined,
    });
  const { sortKey, sortDirection, sortStrategy = StringSort } = sortSettings;

  // Get Processed Data.
  const processedData = useMemo(() => {
    let processedData: T[] = data;

    if (sortKey && sortDirection) {
      processedData = [...processedData];

      // Desc vs Asc
      const descModifier: number = sortDirection === "asc" ? 1 : -1;
      processedData.sort((objA, objB) => {
        if (objA[sortKey] === undefined) {
          return descModifier * -1;
        } else if (objB[sortKey] === undefined) {
          return descModifier;
        }
        return sortStrategy(objA[sortKey], objB[sortKey]) * descModifier;
      });
    }

    return processedData;
  }, [data, sortKey, sortDirection, sortStrategy]);

  // Generate object for interaction
  const contextProps = useMemo(() => {
    return {
      sortSettings,
      setSortSettings,
    };
  }, [sortSettings, setSortSettings]);

  return (
    <RaangeDataTableContext.Provider value={contextProps}>
      <TableStyled responsive {...rest} className={className ? className : ""}>
        {children(processedData)}
      </TableStyled>
    </RaangeDataTableContext.Provider>
  );
};

const TableStyled = styled(Table)`
  thead th {
    color: #a0a5c3;
    border-top: none;
    border-bottom: none;
    white-space: nowrap;
  }

  tbody {
    td,
    th {
      border-top: 1rem solid #f7f8fc;
      background: white;
    }
  }

  .raange-data-table-icons .fas {
    margin-right: 0.2rem;
  }
`;
